<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Search, User, ChatLineRound, ArrowDown, ArrowRight } from '@element-plus/icons-vue'
import { useChatStore } from '/src/stores/chat'
import { useAuthStore } from '/src/stores/auth'

const chatStore = useChatStore()
const authStore = useAuthStore()

const searchQuery = ref('')
const expandedDepts = ref(['开发组'])

const groupedContacts = computed(() => {
  const groups = {}
  chatStore.teamContacts.forEach(contact => {
    const dept = contact.department || '未分组'
    if (!groups[dept]) {
      groups[dept] = []
    }
    groups[dept].push(contact)
  })
  return groups
})

const filteredRecentContacts = computed(() => {
  if (!searchQuery.value) return chatStore.recentContacts
  const query = searchQuery.value.toLowerCase()
  return chatStore.recentContacts.filter(c =>
    c.nickname?.toLowerCase().includes(query) ||
    c.role?.toLowerCase().includes(query)
  )
})

const filteredGroupedContacts = computed(() => {
  if (!searchQuery.value) return groupedContacts.value
  const query = searchQuery.value.toLowerCase()
  const result = {}
  Object.keys(groupedContacts.value).forEach(dept => {
    const filtered = groupedContacts.value[dept].filter(c =>
      c.nickname?.toLowerCase().includes(query) ||
      c.role?.toLowerCase().includes(query)
    )
    if (filtered.length > 0) {
      result[dept] = filtered
    }
  })
  return result
})

const toggleDept = (dept) => {
  const idx = expandedDepts.value.indexOf(dept)
  if (idx > -1) {
    expandedDepts.value.splice(idx, 1)
  } else {
    expandedDepts.value.push(dept)
  }
}

const handleContactClick = (contact) => {
  console.log('发起私聊:', contact)
}

const formatLastContact = (date) => {
  if (!date) return ''
  const now = new Date()
  const diff = now - new Date(date)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 2) return '昨天'
  return `${days}天前`
}

onMounted(() => {
  const teamId = authStore.currentTeam?.id
  if (teamId) {
    chatStore.fetchContacts(teamId)
  }
})

watch(() => chatStore.activeTab, (tab) => {
  if (tab === 'contacts') {
    const teamId = authStore.currentTeam?.id
    if (teamId) {
      chatStore.fetchContacts(teamId)
    }
  }
})

watch(() => authStore.currentTeam, (team) => {
  if (team?.id && chatStore.activeTab === 'contacts') {
    chatStore.fetchContacts(team.id)
  }
})
</script>

<template>
  <div class="contacts-view">
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索联系人..."
        :prefix-icon="Search"
        clearable
        size="small"
      />
    </div>

    <div class="contacts-list">
      <template v-if="filteredRecentContacts.length > 0">
        <div class="section-title">
          <el-icon><User /></el-icon>
          <span>近期联系人</span>
        </div>
        <div
          v-for="contact in filteredRecentContacts"
          :key="contact.id"
          class="contact-item"
          @click="handleContactClick(contact)"
        >
          <el-avatar :size="40" :src="contact.avatar">
            {{ contact.nickname?.[0] || '?' }}
          </el-avatar>
          <div class="contact-info">
            <div class="contact-name">{{ contact.nickname }}</div>
            <div class="contact-meta">
              <span>{{ contact.role }}</span>
              <span class="separator">|</span>
              <span>最后联系: {{ formatLastContact(contact.lastContact) }}</span>
            </div>
          </div>
          <el-button size="small" type="primary" text>
            <el-icon><ChatLineRound /></el-icon>
          </el-button>
        </div>
      </template>

      <template v-if="Object.keys(filteredGroupedContacts).length > 0">
        <div class="section-title">
          <el-icon><User /></el-icon>
          <span>团队通讯录</span>
        </div>
        <div
          v-for="(contacts, dept) in filteredGroupedContacts"
          :key="dept"
          class="dept-group"
        >
          <div class="dept-header" @click="toggleDept(dept)">
            <el-icon v-if="expandedDepts.includes(dept)">
              <ArrowDown />
            </el-icon>
            <el-icon v-else>
              <ArrowRight />
            </el-icon>
            <span class="dept-name">{{ dept }}</span>
            <span class="dept-count">{{ contacts.length }}</span>
          </div>
          <Transition name="expand">
            <div v-if="expandedDepts.includes(dept)" class="dept-members">
              <div
                v-for="contact in contacts"
                :key="contact.id"
                class="contact-item"
                @click="handleContactClick(contact)"
              >
                <el-avatar :size="36" :src="contact.avatar">
                  {{ contact.nickname?.[0] || '?' }}
                </el-avatar>
                <div class="contact-info">
                  <div class="contact-name">{{ contact.nickname }}</div>
                  <div class="contact-meta">
                    {{ contact.role }}
                  </div>
                </div>
                <el-button size="small" type="primary" text>
                  <el-icon><ChatLineRound /></el-icon>
                </el-button>
              </div>
            </div>
          </Transition>
        </div>
      </template>

      <div
        v-if="filteredRecentContacts.length === 0 && Object.keys(filteredGroupedContacts).length === 0"
        class="empty-state"
      >
        <el-icon :size="48" color="#d1d5db"><User /></el-icon>
        <p>暂无联系人</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.contacts-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-bar {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.contacts-list {
  flex: 1;
  overflow-y: auto;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.contact-item:hover {
  background: #f3f4f6;
}

.contact-info {
  flex: 1;
  min-width: 0;
}

.contact-name {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.contact-meta {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
}

.contact-meta .separator {
  margin: 0 6px;
}

.dept-group {
  border-bottom: 1px solid #f3f4f6;
}

.dept-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.dept-header:hover {
  background: #f3f4f6;
}

.dept-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.dept-count {
  font-size: 12px;
  color: #9ca3af;
}

.dept-members {
  background: #fafafa;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.empty-state p {
  margin: 12px 0;
}
</style>
